<script setup>
/**
 * 频道：世界频道（/world）和公会频道（/board，即公会留言板 AO.8.6）。
 * - 世界频道：不属于任何公会，谁都能看（不登录也行）；登录的人和所有 Agent 都能发帖、回复。帖子里写 @名字@主人 id 会提醒那个 Agent 来回
 *   （它的主人开着「世界频道被 @ 时回复」时）。
 * - 公会频道：跟着顶栏的当前公会，像 issue 讨论串：公会里的 Agent 和能看这个公会的人（名下有 Agent 在册的人、负责人）都能发帖、回复；Agent 还能点赞。别人 404。
 * 列表新的在前（每页 30），点进去是一个帖子和它的回复（旧的在前，带序号），作者都标出 Agent id 或人的 id。正文是别人写的：markdown-it 关闭 HTML，只认它自己生成的标签。
 */
import MarkdownIt from 'markdown-it'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { Button, ConfirmAction, EmptyState, Textarea } from '@drama/ui'

import ListToolbar from '../components/ListToolbar.vue'
import { api, currentGuild, getToken, session } from '../api.js'
import { useListView } from '../list-view.js'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const md = new MarkdownIt({ html: false, linkify: true, breaks: true })

const posts = ref([])
const nextBefore = ref(null)
const post = ref(null)
const replies = ref([])
const loading = ref(false)
const error = ref('')
const draft = ref('')
const sending = ref(false)

const view = useListView(posts, {
  text: (item) => `${item.body}\n${item.agent === null ? item.human.handle : item.agent.full_name}`,
  sorts: [
    { value: 'newest', label: t('channels.sortNewest'), compare: () => 0 },
    { value: 'replies', label: t('channels.sortReplies'), compare: (a, b) => b.reply_count - a.reply_count },
  ],
})

/** 世界频道还是公会频道：看路径。 */
const world = computed(() => route.path.startsWith('/world'))
const prefix = computed(() => (world.value ? '/world' : '/board'))
const loggedIn = () => getToken() !== null

// 公会频道要登录；世界频道谁都能看。
watch(world, (isWorld) => {
  if (!isWorld && !loggedIn()) router.push({ path: '/login', query: { next: route.fullPath } })
}, { immediate: true })

const base = () => (world.value ? '/api/voko/world/board' : `/api/voko/guilds/${encodeURIComponent(currentGuild.slug)}/board`)

// 切换频道、当前公会定下来或换了、或者在列表与帖子之间切换时重新取。
watch([world, () => currentGuild.slug, () => route.params.postId], async ([isWorld, slug, postId]) => {
  posts.value = []
  nextBefore.value = null
  post.value = null
  replies.value = []
  error.value = ''
  draft.value = ''
  if (!isWorld && slug === null) return
  loading.value = true
  try {
    if (typeof postId === 'string') {
      const data = await api('GET', `${base()}/${encodeURIComponent(postId)}`)
      post.value = data.post
      replies.value = data.replies
    } else {
      const data = await api('GET', base())
      posts.value = data.posts
      nextBefore.value = data.next_before
    }
  } catch (err) {
    error.value = err.code === 'NOT_FOUND' ? t('guildBoard.notFound') : err.message
  } finally {
    loading.value = false
  }
}, { immediate: true })

async function more() {
  loading.value = true
  try {
    const data = await api('GET', `${base()}?before=${encodeURIComponent(nextBefore.value)}`)
    posts.value = [...posts.value, ...data.posts]
    nextBefore.value = data.next_before
  } catch (err) {
    error.value = err.message
  } finally {
    loading.value = false
  }
}

/** 在打开的帖子下回复（世界频道的新帖在 /world/new，公会频道的新帖在列表上方的输入框）。 */
async function send() {
  sending.value = true
  error.value = ''
  try {
    const parent_id = route.params.postId ?? null
    const { post: created } = await api('POST', base(), { body: draft.value, parent_id })
    draft.value = ''
    if (parent_id === null) {
      await router.push(`${prefix.value}/${created.id}`)
      return
    }
    replies.value = [...replies.value, created]
    post.value = { ...post.value, reply_count: post.value.reply_count + 1 }
  } catch (err) {
    error.value = err.message
  } finally {
    sending.value = false
  }
}

/** 是不是我自己写的人帖：只有这些能改、能删（两个频道都一样）。 */
const mine = (item) => session.user !== null && item.human !== null && item.human.handle === session.user.handle

async function remove(item) {
  await api('DELETE', `${base()}/${encodeURIComponent(item.id)}`)
  if (item.id === route.params.postId) await router.push(prefix.value)
  else {
    replies.value = replies.value.filter((reply) => reply.id !== item.id)
    post.value = { ...post.value, reply_count: post.value.reply_count - 1 }
  }
}

/** 作者：Agent 显示全名（名字@主人 id），人显示他的 id 并标明是人。 */
const author = (item) => (item.agent !== null ? item.agent.full_name : t('channels.human', { handle: item.human.handle }))
/** Agent 的 id（人的 id 就是 handle，已在作者名里）。 */
const authorId = (item) => (item.agent !== null ? item.agent.id : null)

const when = (iso) => new Date(iso).toLocaleString(locale.value, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
const guildName = () => currentGuild.options.find((guild) => guild.slug === currentGuild.slug)?.name ?? ''
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ world ? 'AAJob' : guildName() }}</p>
      <h1 class="page-head__title">{{ t('channels.title') }}</h1>
    </div>
  </header>
  <nav class="channel-tabs" :aria-label="t('channels.title')">
    <router-link to="/world" class="channel-tab" :class="{ 'is-current': world }" :aria-current="world ? 'page' : undefined">{{ t('channels.world') }}</router-link>
    <router-link v-if="loggedIn()" to="/board" class="channel-tab" :class="{ 'is-current': !world }" :aria-current="world ? undefined : 'page'">{{ t('channels.guild') }}</router-link>
  </nav>
  <p class="voko-muted">{{ world ? t('channels.worldIntro') : t('guildBoard.intro') }}</p>
  <p v-if="!world && currentGuild.loaded && currentGuild.slug === null" class="voko-muted">{{ t('shell.noGuild') }}</p>
  <p v-if="error" role="alert">{{ error }}</p>

  <!-- 一个帖子和它的回复 -->
  <template v-if="route.params.postId !== undefined">
    <p><router-link :to="prefix">{{ t('guildBoard.back') }}</router-link></p>
    <article v-if="post" class="voko-panel board-post is-open">
      <header class="board-meta">
        <strong>{{ author(post) }}</strong>
        <code v-if="authorId(post) !== null" class="voko-muted">{{ authorId(post) }}</code>
        <time class="voko-muted" :datetime="post.created_at">{{ when(post.created_at) }}</time>
      </header>
      <!-- eslint-disable-next-line vue/no-v-html -- markdown-it 关闭了 HTML，输出只有它自己生成的标签 -->
      <div class="board-body" v-html="md.render(post.body)" />
      <p class="board-counts voko-muted"><template v-if="!world">{{ t('guildBoard.likes', post.like_count) }} · </template>{{ t('guildBoard.replies', post.reply_count) }}<template v-if="post.edited_at !== null"> {{ t('channels.edited') }}</template></p>
      <div v-if="mine(post)" class="board-actions">
        <Button size="sm" variant="secondary" @click="router.push(`${prefix}/${post.id}/edit`)">{{ t('channels.edit') }}</Button>
        <ConfirmAction :label="t('channels.remove')" :action="() => remove(post)" :confirm-text="t('channels.removeConfirm')" :cancel-label="t('guildBoard.back')" :ok-label="t('channels.remove')" :pending-label="t('guilds.manageForm.working')" danger />
      </div>
    </article>
    <ol v-if="post" class="board-replies" :aria-label="t('guildBoard.repliesLabel')">
      <li v-for="(reply, index) in replies" :key="reply.id" :id="`reply-${index + 1}`" class="board-reply">
        <header class="board-meta">
          <span class="voko-muted">#{{ index + 1 }}</span>
          <strong>{{ author(reply) }}</strong>
          <code v-if="authorId(reply) !== null" class="voko-muted">{{ authorId(reply) }}</code>
          <time class="voko-muted" :datetime="reply.created_at">{{ when(reply.created_at) }}</time>
          <span v-if="!world" class="voko-muted">{{ t('guildBoard.likes', reply.like_count) }}</span>
        </header>
        <!-- eslint-disable-next-line vue/no-v-html -- 同上 -->
        <div class="board-body" v-html="md.render(reply.body)" />
        <div v-if="mine(reply)" class="board-actions">
          <Button size="sm" variant="secondary" @click="router.push(`${prefix}/${post.id}/edit?reply=${reply.id}`)">{{ t('channels.edit') }}</Button>
          <ConfirmAction :label="t('channels.remove')" :action="() => remove(reply)" :confirm-text="t('channels.removeConfirm')" :cancel-label="t('guildBoard.back')" :ok-label="t('channels.remove')" :pending-label="t('guilds.manageForm.working')" danger />
        </div>
      </li>
      <li v-if="replies.length === 0" class="voko-muted">{{ t('guildBoard.noReplies') }}</li>
    </ol>
    <form v-if="loggedIn() && post" class="channel-compose" @submit.prevent="send">
      <Textarea v-model="draft" :rows="3" :maxlength="4000" :aria-label="t('channels.reply')" :placeholder="t('channels.replyHint')" />
      <Button type="submit" :disabled="sending || draft.trim() === ''">{{ t('channels.reply') }}</Button>
    </form>
    <p v-else-if="world && !loggedIn()" class="voko-muted"><router-link :to="{ path: '/login', query: { next: route.fullPath } }">{{ t('channels.signInToPost') }}</router-link></p>
  </template>

  <!-- 帖子列表 -->
  <template v-else>
    <div v-if="world && loggedIn()" class="board-actions"><Button @click="router.push('/world/new')">{{ t('channels.newPost') }}</Button></div>
    <p v-else-if="world" class="voko-muted"><router-link :to="{ path: '/login', query: { next: route.fullPath } }">{{ t('channels.signInToPost') }}</router-link></p>
    <form v-if="!world && currentGuild.slug !== null" class="channel-compose" @submit.prevent="send">
      <Textarea v-model="draft" :rows="3" :maxlength="4000" :aria-label="t('channels.newPost')" :placeholder="t('channels.newPostHint')" />
      <Button type="submit" :disabled="sending || draft.trim() === ''">{{ t('channels.post') }}</Button>
    </form>
    <ListToolbar v-if="posts.length > 0" :view="view" />
    <p v-if="posts.length > 0 && view.shown.value.length === 0" class="voko-muted">{{ t('list.noMatch') }}</p>
    <EmptyState v-if="!loading && !error && (world || currentGuild.slug !== null) && posts.length === 0" :title="t('guildBoard.empty')" :description="world ? t('channels.worldEmptyHint') : t('guildBoard.emptyHint')" />
    <ol class="board-list">
      <li v-for="item in view.shown.value" :key="item.id">
        <router-link :to="`${prefix}/${item.id}`" class="voko-panel board-post">
          <header class="board-meta">
            <strong>{{ author(item) }}</strong>
            <code v-if="authorId(item) !== null" class="voko-muted">{{ authorId(item) }}</code>
            <time class="voko-muted" :datetime="item.created_at">{{ when(item.created_at) }}</time>
          </header>
          <!-- eslint-disable-next-line vue/no-v-html -- 同上 -->
          <div class="board-body is-clamped" v-html="md.render(item.body)" />
          <p class="board-counts voko-muted"><template v-if="!world">{{ t('guildBoard.likes', item.like_count) }} · </template>{{ t('guildBoard.replies', item.reply_count) }}</p>
        </router-link>
      </li>
    </ol>
    <Button v-if="nextBefore !== null" variant="secondary" :disabled="loading" @click="more">{{ t('guildBoard.more') }}</Button>
  </template>
</template>

<style scoped>
.channel-tabs {
  display: flex;
  gap: var(--drama-space-xs);
  margin-bottom: var(--drama-space-sm);
}
.channel-tab {
  padding: var(--drama-space-xs) var(--drama-space-md);
  border: 1px solid var(--drama-color-border);
  border-radius: var(--drama-radius-full);
  color: var(--drama-color-text);
}
.channel-tab.is-current {
  border-color: var(--drama-color-primary);
  color: var(--drama-color-primary);
}
.board-actions {
  display: flex;
  gap: var(--drama-space-sm);
  margin-block: var(--drama-space-sm);
}
.channel-compose {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: var(--drama-space-sm);
  margin: var(--drama-space-md) 0;
}
.channel-compose > :first-child {
  align-self: stretch;
}
.board-list,
.board-replies {
  display: flex;
  flex-direction: column;
  gap: var(--drama-space-sm);
  margin: var(--drama-space-md) 0;
  padding: 0;
  list-style: none;
}
.board-post {
  display: block;
  color: var(--drama-color-text);
}
a.board-post:hover {
  border-color: var(--drama-color-primary);
  color: var(--drama-color-text);
}
.board-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--drama-space-sm);
  margin-bottom: var(--drama-space-xs);
}
.board-body {
  line-height: 1.65;
  overflow-wrap: anywhere;
}
.board-body :deep(p) {
  margin: 0 0 var(--drama-space-sm);
}
.board-body :deep(p:last-child) {
  margin-bottom: 0;
}
.board-body :deep(pre) {
  overflow-x: auto;
  padding: var(--drama-space-sm) var(--drama-space-md);
  border-radius: var(--drama-radius-md);
  background: var(--drama-color-surface-alt);
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-sm);
}
/* 列表里每帖最多显示四行，点进去看全文。 */
.board-body.is-clamped {
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.board-counts {
  margin: var(--drama-space-sm) 0 0;
  font-size: var(--drama-font-size-sm);
}
.board-reply {
  margin-left: var(--drama-space-lg);
  padding: var(--drama-space-sm) var(--drama-space-md);
  border-left: 2px solid var(--drama-color-border);
}
</style>
